import { useState, useEffect, useCallback } from 'react'
import Input from 'components/form/Input'
import {
  maxLength,
  minLength,
  hasNumbers,
  hasLowercase,
  hasUppercase,
  hasSpecial,
  PASSED,
} from 'components/form/validations'
import styled from 'styled-components/macro'
import { Caption } from 'components/layout/text'
import Icon from 'components/misc/Icon'
import { useLocalization } from 'localization'

const defaultRequirements = [
  { titleKey: 'dweb_password_requirement_lowercase', validate: hasLowercase },
  { titleKey: 'dweb_password_requirement_number', validate: hasNumbers },
  { titleKey: 'dweb_password_requirement_uppercase', validate: hasUppercase },
  { titleKey: 'dweb_password_requirement_min_8_characters', validate: minLength(8) },
  { titleKey: 'dweb_password_requirement_special_character', validate: hasSpecial },
  { titleKey: 'dweb_password_requirement_max_50_characters', validate: maxLength(50) },
]
const defaultName = 'password'

const getRequirementId = (item) => item.titleKey || item.title
const getRequirementTitle = (item, t) => (item.titleKey ? t(item.titleKey) : item.title)

const ValidationHints = styled.div`
  display: flex;
  flex-direction: row;
  text-align: left;
  flex-wrap: wrap;
  width: 100%;
`

const HintContainer = styled.div`
  width: 50%;
  word-break: none;
  display: flex;
  flex-direction: row;
  flex-wrap: no-wrap;
  margin-top: 10px;
  align-items: center;
`

const CheckIcon = styled(Icon).attrs((props) => ({
  symbol: 'check-circle',
  palette: props.passed ? 'success' : 'grey',
  shade: props.passed ? 'base' : 'light',
}))`
  width: 1.3rem;
  height: 1.3rem;
  margin-right: 5px;
`

const Hint = ({ title, passed }) => (
  <HintContainer>
    <CheckIcon passed={passed} />
    <Caption weight="500" emphasis="low">
      {title}
    </Caption>
  </HintContainer>
)

const PasswordInput = ({
  requirements = defaultRequirements,
  name = defaultName,
  label,
  required = true,
  onChange,
  onFocus,
  onBlur,
  ...rest
}) => {
  const { t } = useLocalization()
  const [value, setValue] = useState()
  const [focused, setFocused] = useState(false)
  const [wasFocused, setWasFocused] = useState(false)
  const [hintsStatus, setHintsStatus] = useState(() => {
    const initial = {}
    requirements.map(getRequirementId).forEach((id) => {
      initial[id] = false
    })
    return initial
  })

  const updateHintsStatus = useCallback(
    (value) => {
      const newHintsStatus = {}
      requirements.forEach((item) => {
        newHintsStatus[getRequirementId(item)] = value && item.validate(value) === PASSED
      })
      setHintsStatus(newHintsStatus)
    },
    [requirements]
  )

  const handleChange = useCallback(
    (event) => {
      const {
        target: { value },
      } = event
      setValue(value)
      if (onChange) onChange(event)
    },
    [onChange]
  )

  const handleFocus = useCallback(
    (event) => {
      setFocused(true)
      if (onFocus) onFocus(event)
    },
    [onFocus]
  )

  const handleBlur = useCallback(
    (event) => {
      setFocused(false)
      if (onBlur) onBlur(event)
    },
    [onBlur]
  )

  useEffect(() => {
    if (focused && !wasFocused) setWasFocused(true)
    if (value || (!value && focused)) updateHintsStatus(value)
  }, [value, focused, wasFocused, updateHintsStatus])

  const allPassed = !requirements.find((item) => hintsStatus[getRequirementId(item)] === false)
  let validations = requirements.reduce(
    (val, req, index) => (index > 0 ? val.and(req.validate) : val),
    requirements[0].validate
  )
  if (required) validations = validations.required()

  return (
    <>
      <Input
        {...rest}
        password
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        label={label ?? t('dweb_common_password')}
        name={name}
        validate={validations}
      />
      {focused || (!allPassed && wasFocused) ? (
        <ValidationHints>
          {requirements.map((item) => (
            <Hint
              key={getRequirementId(item)}
              title={getRequirementTitle(item, t)}
              passed={hintsStatus[getRequirementId(item)]}
            />
          ))}
        </ValidationHints>
      ) : (
        ''
      )}
    </>
  )
}

export default PasswordInput
